<template>
  <base-card>
    <div class="py-1 px-2">
      <div class="mb-n1">
        <base-skeleton
          v-if="loading"
          class="p-0"
        >
          <div
            class="skeleton__item py-3"
            style="max-width: 350px"
          />
        </base-skeleton>
        <div
          v-else
          class="d-flex"
        >
          <base-text size="l">
            {{ subject ? subject : 'Untitled' | striphtml }}
          </base-text>
          <div class="ml-2 align-self-center">
            <base-badge type="green">
              {{ getBadge() }}
            </base-badge>
          </div>
        </div>
      </div>
      <hr>
      <base-skeleton
        v-if="loading"
        class="p-0 d-flex justify-content-start align-items-center"
      >
        <div
          class="skeleton__item mt-2"
          style="max-width: 80px"
        />

        <div
          class="skeleton__item ml-2 mr-3"
          style="max-width: 200px"
        />

        <div
          class="skeleton__item mt-2"
          style="max-width: 80px"
        />
        <div
          v-for="n in 3"
          :key="`recipient_skel_${n}`"
          class="skeleton__item ml-2"
          :style="`max-width: 100px; opacity: ${1/n};`"
        />
      </base-skeleton>
      <div v-else>
        <!-- Email, Link or PDF -->
        <template v-if="!isAppointment">
          <base-text
            inline
            size="m"
            light
            class=""
          >
            <span
              v-if="isAdminMode"
              class="mr-2"
            >{{ senderNameBlurb }}</span>
            <span>
              Delivered:
            </span>
            <span>
              {{ item.time_iso | nicedate("long") }}
            </span>
          </base-text>
          <base-text
            inline
            size="m"
            class="ml-3"
          >
            Sent to:
          </base-text>
          <span
            v-for="(group, index) in (item.segments ? item.segments.groups : [])"
            :key="group.pk"
          >
            <base-text
              inline
              size="m"
              medium
              underline
              class="clickable"
              @click.native="goToActivity(group.pk)"
            >
              {{ group.name }}
            </base-text>
            <span v-if="item.segments.groups">
              {{ (item.segments.groups.length > 1 && index < item.segments.groups.length
                -1)
                ? ','
                : ''
              }}
            </span>
          </span>

          {{ item.segments && item.segments.customers > 0
            && item.segments.groups && item.segments.groups.length > 0 ? '+' : '' }}
          <base-text
            v-if="item.segments && item.segments.customers > 0
              || (item.segments && typeof(item.segments.customers) === 'string')"
            inline
            size="m"
            medium
            underline
            class="clickable"
            @click.native="goToActivity(0)"
          >
            {{ getClients() }}
          </base-text>
        </template>
        <!-- Appointment -->
        <template v-else>
          <div class="d-flex">
            <base-text
              inline
              size="m"
              light
            >
              <span
                class="mr-2"
              >
                {{ item.start_date | nicedate("long") }}
              </span>
            </base-text>
            <base-text
              light
              size="m"
              class="d-flex"
            >
              <span>
                With:
              </span>
              &nbsp;
              <base-text
                semibold
                underline
                size="m"
                class="clickable"
                @click.native="goToActivity()"
              >
                {{ getInvitees() }}
              </base-text>
            </base-text>
            <base-text
              v-if="item.duration"
              inline
              light
              size="m"
              class="ml-3"
            >
              Duration: {{ item.duration }}
            </base-text>
          </div>
        </template>
      </div>
    </div>
  </base-card>
</template>
<script>
import { APPOINTMENT_TYPES } from '@/_utilities/Enums';

export default {
  name: 'OutReachHeading',
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    subject() {
      return this.item.edit ? this.item.edit.subject : this.item.subject;
    },
    /** For normal edits, corresponds to the outreach id.
     * For appointments, it's the appointment's id. */
    reportId() {
      return this.$route.params.id || this.$route.params.appointmentId;
    },
    // duration() {
    //   let duration = '';
    //   if (this.item.duration?.hours) {
    //     duration += `${this.item.duration.hours}hours `;
    //   }
    //   duration += this.item.duration ? `${this.item.duration.minutes}min` : '--';
    //   return duration;
    // },

    senderNameBlurb() {
      let blurb = '';
      if (this.item.sender && this.item.sender.user) {
        const usr = this.item.sender.user;
        blurb = `By: ${[usr.first_name, usr.last_name].join(' ')}`;
      }
      return blurb;
    },
    isAdminMode() {
      return this.$store.state.account.isAdminMode;
    },
    isAppointment() {
      return Object.prototype.hasOwnProperty.call(this.item, 'appointment_type');
    },
  },
  methods: {
    /**
     * Return the name of the clients or the number of clients.
     * Important: Fix the backend to retreive a name|email|phone or whatever the client has.
     */
    getClients() {
      if (typeof (this.item.segments.customers) === 'string') {
        return this.item.edit.sent_to.display;
      }
      return `${this.item.segments.customers} Client${this.item.segments.customers === 1 ? '' : 's'}`;
    },
    getBadge() {
      if (this.isAppointment) {
        const labels = {};
        labels[APPOINTMENT_TYPES.VIRTUAL] = 'Virtual';
        labels[APPOINTMENT_TYPES.LIVESTREAM] = 'Live Stream';
        labels[APPOINTMENT_TYPES.IN_PERSON] = 'In Person';
        labels[APPOINTMENT_TYPES.RECOMMENDATION] = 'Recommendation';
        return labels[this.item.appointment_type];
      }
      const values = {
        email: 'Email',
        pdf: 'PDF',
        wechat: 'WeChat',
        html: 'Share Link',
        embed: 'Embed Code',
      };
      return values[this.item.display];
    },
    getInvitees() {
      if (!this.isAppointment) {
        return '';
      }

      // Separate invitees from anons
      const nAnons = this.item.invitations.filter((i) => i.customer === null).length;
      const nCustomers = this.item.invitations.filter((i) => i.customer).length;

      const anonsLabel = nAnons > 0 ? ` + ${nAnons} anonymous participant${nAnons > 1 ? 's' : ''}` : '';
      let customersLabel = '';
      if (nCustomers > 0) {
        if (nCustomers === 1) {
          const { customer } = this.item.invitations[0];
          customersLabel = `${customer.first_name} ${customer.last_name}`.trim();
          customersLabel = customersLabel.length ? customersLabel : this.$t('clients.singleClient');
        } else {
          customersLabel = `${nCustomers} Clients`;
        }
      }
      return `${customersLabel}${anonsLabel}`;
    },
    calculateTime() {
      // tbd
      return '';
    },
    goToActivity(group = undefined) {
      this.$router.push({
        path: `/reports/${this.isAppointment ? 'appointment' : 'outreach'}/${this.reportId}/activity/`,
        query: { group },
      });
    },
  },
};
</script>
<style lang="scss"
  scoped>
.clickable {
  cursor: pointer;

  &:hover {
    ::v-deep .base-text {
      text-decoration: underline;
    }
  }
}

::v-deep .card-body {
  background-color: $gray-light-3;
}
</style>
